import React, { createContext, useContext, useState, useCallback } from 'react'

export interface WholesaleReservation {
  reservation_id: string
  amount: number
  expires_at: string
}

export interface ShippingAddress {
  street: string
  apt: string
  city: string
  state: string
  zip: string
}

export interface WholesaleQuote {
  subtotal: number
  shippingCost: number
  taxAmount: number
  total: number
  zone: number
  shippingEstimated: boolean
  discountAmount: number
  couponCode: string | null
  couponValid: boolean
  upsShippingCost: number
  localDeliveryAvailable: boolean
  localDeliveryCost: number
  recommendedMethod: 'ups' | 'local'
}

export interface WholesaleKit {
  size: number
  price: number
  label: string
}

interface WholesaleContextType {
  // Kit selection — always the 30-pack, but kept as state for parity with taste
  selectedKit: WholesaleKit | null
  setSelectedKit: (kit: WholesaleKit | null) => void

  // Quantity
  quantity: number
  setQuantity: (q: number) => void

  // Coupon
  couponCode: string
  couponInput: string
  setCouponInput: (code: string) => void
  applyCoupon: () => void
  removeCoupon: () => void
  couponError: string | null
  couponChecking: boolean
  couponDiscountType: string | null

  // Delivery method
  deliveryMethod: 'ups' | 'local'
  setDeliveryMethod: (m: 'ups' | 'local') => void

  // Live shipping + tax quote
  quote: WholesaleQuote | null
  quoteLoading: boolean
  fetchQuote: () => void
  clearQuote: () => void

  // Total
  totalAmount: number

  // Customer info
  customerEmail: string
  setCustomerEmail: (email: string) => void
  customerName: string
  setCustomerName: (name: string) => void

  // Shipping address
  shippingAddress: ShippingAddress
  setShippingAddress: (address: ShippingAddress) => void

  // Reservation
  reservation: WholesaleReservation | null
  createReservation: (amount: number) => Promise<void>

  // State
  loading: boolean
  error: string | null
  clearError: () => void
}

const WholesaleContext = createContext<WholesaleContextType | null>(null)

export const useWholesale = () => {
  const context = useContext(WholesaleContext)
  if (!context) {
    throw new Error('useWholesale must be used within a WholesaleProvider')
  }
  return context
}

export const WholesaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedKit, setSelectedKit] = useState<WholesaleKit | null>({
    size: 30,
    price: 375.00,
    label: '30 Tails',
  })
  const [quantity, setQuantity] = useState(1)
  const [couponCode, setCouponCode] = useState('')
  const [couponInput, setCouponInput] = useState('')
  const [couponError, setCouponError] = useState<string | null>(null)
  const [couponChecking, setCouponChecking] = useState(false)
  const [couponDiscountType, setCouponDiscountType] = useState<string | null>(null)
  const [deliveryMethod, setDeliveryMethod] = useState<'ups' | 'local'>('ups')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  })
  const [reservation, setReservation] = useState<WholesaleReservation | null>(null)
  const [quote, setQuote] = useState<WholesaleQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Calculate total amount — prefer the server quote's total (which includes
  // shipping + tax + coupon discount) when one exists; otherwise fall back to
  // the kit subtotal × quantity.
  const productSubtotal = (selectedKit?.price || 0) * quantity
  const totalAmount = quote ? quote.total : productSubtotal

  // Fetch a live shipping + tax quote for the current kit and destination.
  // Only fires when a state and a complete 5-digit ZIP are present.
  const fetchQuote = useCallback(() => {
    const { state, zip } = shippingAddress
    if (!state || !/^\d{5}$/.test(zip)) {
      setQuote(null)
      return
    }
    let cancelled = false
    setQuoteLoading(true)
    fetch('/.netlify/functions/get-wholesale-quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state, zip, quantity, couponCode: couponCode || undefined, deliveryMethod }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Quote request failed')
        const data = await res.json()
        if (cancelled) return
        setQuote({
          subtotal: data.subtotal,
          shippingCost: data.shippingCost,
          taxAmount: data.taxAmount,
          total: data.total,
          zone: data.zone,
          shippingEstimated: !!data.shippingEstimated,
          discountAmount: data.discountAmount ?? 0,
          couponCode: data.couponCode ?? null,
          couponValid: !!data.couponValid,
          upsShippingCost: data.upsShippingCost ?? 0,
          localDeliveryAvailable: !!data.localDeliveryAvailable,
          localDeliveryCost: data.localDeliveryCost ?? 0,
          recommendedMethod: data.recommendedMethod ?? 'ups',
        })
      })
      .catch(() => {
        if (!cancelled) setQuote(null)
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shippingAddress, quantity, couponCode, deliveryMethod])

  // Create a reservation for the order amount
  const createReservation = useCallback(async (amt: number) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/.netlify/functions/create-taste-reservation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, reservationMinutes: 15, maxAmount: 25000 }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create reservation')
      }

      const data = await response.json()
      setReservation(data)
    } catch (err: any) {
      console.error('Error creating reservation:', err)
      setError(err.message || 'Failed to create reservation')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const applyCoupon = useCallback(async () => {
    const code = couponInput.trim().toUpperCase()
    if (!code) {
      setCouponError('Enter a code')
      return
    }
    setCouponError(null)
    setCouponChecking(true)
    try {
      const res = await fetch('/.netlify/functions/validate-wholesale-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.valid) {
        // Reject bogus / expired / inactive codes on the spot. Never set
        // couponCode for an unverified code.
        setCouponCode('')
        setCouponError('That code isn’t valid.')
        return
      }
      // Server confirmed the code is real, active, and not expired. The quote
      // RPC still re-validates it against the delivery method when it runs.
      setCouponCode(data.code)
      setCouponDiscountType(data.discountType || null)
    } catch {
      setCouponCode('')
      setCouponError('Couldn’t check that code. Please try again.')
    } finally {
      setCouponChecking(false)
    }
  }, [couponInput])

  const removeCoupon = useCallback(() => {
    setCouponCode('')
    setCouponInput('')
    setCouponError(null)
    setCouponDiscountType(null)
  }, [])

  const clearQuote = useCallback(() => {
    setQuote(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return (
    <WholesaleContext.Provider
      value={{
        selectedKit,
        setSelectedKit,
        quantity,
        setQuantity,
        couponCode,
        couponInput,
        setCouponInput,
        applyCoupon,
        removeCoupon,
        couponError,
        couponChecking,
        couponDiscountType,
        deliveryMethod,
        setDeliveryMethod,
        quote,
        quoteLoading,
        fetchQuote,
        clearQuote,
        totalAmount,
        customerEmail,
        setCustomerEmail,
        customerName,
        setCustomerName,
        shippingAddress,
        setShippingAddress,
        reservation,
        createReservation,
        loading,
        error,
        clearError,
      }}
    >
      {children}
    </WholesaleContext.Provider>
  )
}

export default WholesaleProvider
